import type { JSX } from 'react';

import { useQuery } from '@apollo/client/react';
import { Link } from 'react-router-dom';

import { useTenant } from '../../contexts/TenantContext';
import { SlackConnectionStatus } from '../../graphql/enums.graphql';
import { SLACK_STATUS_COPY, isSendable } from '../../pages/Settings/Slack/slackStatus';

import styles from './SlackHealthBanner.module.css';

import SLACK_HEALTH from './queries/slackHealth.query.graphql';
import type { SlackHealthQuery } from './queries/slackHealth.query.graphql';

/**
 * App-wide notice when Slack has stopped delivering.
 *
 * Not dismissible, deliberately. While this is showing, every check-in prompt
 * and cycle announcement the product would have sent is not arriving, and the
 * people who would have noticed are precisely the ones not being messaged —
 * so nobody reports it. The banner is the only thing that surfaces a silent
 * outage, and a dismissed banner is a silent outage with extra steps.
 *
 * Shown only for a tenant that has connected Slack at some point: a tenant
 * that never did is not missing anything.
 */
/** Who can act on a broken connection — the same two roles the API treats as admin. */
const ADMIN_ROLES = new Set(['owner', 'admin']);

export function SlackHealthBanner(): JSX.Element | null {
  // For admins only. The status itself is readable by every member, but a
  // member can do nothing about a revoked token, and a banner they cannot act
  // on is noise on every page. The API still refuses them every Slack action.
  const { selectedTenant, availableTenants } = useTenant();
  const membership = availableTenants.find((t) => t.tenantId === selectedTenant?.id);
  const isAdmin = !!membership && ADMIN_ROLES.has(membership.role);

  // Its own status-only selection, not the settings page's query: this polls
  // for every signed-in user, and the mapping counts that page shows cost a
  // Slack directory read each time they are asked for.
  const { data } = useQuery<SlackHealthQuery>(SLACK_HEALTH, {
    skip: !isAdmin,
    fetchPolicy: 'cache-first',
    // A connection that dies mid-session is the case this exists for, so it is
    // re-read periodically rather than only on mount.
    pollInterval: 5 * 60 * 1000,
    // A failed poll is not worth surfacing anywhere; it just means no banner
    // until the next one succeeds.
    errorPolicy: 'ignore',
  });

  if (!isAdmin) return null;
  const workspace = data?.slackWorkspace;
  if (!workspace) return null;

  // Never connected, or deliberately disconnected: nothing is broken.
  if (workspace.status === SlackConnectionStatus.Disconnected) return null;
  if (workspace.status === SlackConnectionStatus.Connected) return null;

  const copy = SLACK_STATUS_COPY[workspace.status];
  const degraded = isSendable(workspace.status);

  return (
    <div
      className={degraded ? styles.BannerWarning : styles.BannerDanger}
      role="status"
      aria-live="polite"
    >
      <span className={styles.Text}>
        <strong>Slack: {copy.label}.</strong> {workspace.statusReason || copy.explanation}
      </span>
      <Link className={styles.Action} to="/settings/slack">
        Open Slack settings
      </Link>
    </div>
  );
}
